// src/components/DemoLoginService.js
// Service de connexion et gestion du compte de démonstration - VERSION SIMPLIFIÉE

import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';

const auth = getAuth();
const db = getFirestore();

// Configuration du compte de démonstration
const DEMO_ACCOUNT = {
  email: 'service@aladinn.fr',
  password: process.env.REACT_APP_DEMO_PASSWORD || 'DemoAladinn2024!',
};

class DemoLoginService {
  constructor() {
    this.isConnecting = false;
    this.connectionPromise = null;
  }

  // Connexion simple au compte de démonstration
  async loginToDemo() {
    console.log('🎭 Tentative de connexion au compte démo...');
    
    // Éviter les connexions multiples
    if (this.isConnecting && this.connectionPromise) {
      console.log('🔄 Connexion déjà en cours, attente...');
      return this.connectionPromise;
    }

    // Si déjà connecté au compte démo, pas besoin de se reconnecter
    const currentUser = auth.currentUser;
    if (currentUser && currentUser.email === DEMO_ACCOUNT.email) {
      console.log('✅ Déjà connecté au compte démo:', currentUser.email);
      return currentUser;
    }

    this.isConnecting = true;
    this.connectionPromise = this._performDemoLogin();
    
    try {
      const result = await this.connectionPromise;
      return result;
    } finally {
      this.isConnecting = false;
      this.connectionPromise = null;
    }
  }

  async _performDemoLogin() {
    try {
      console.log('🔐 Connexion au compte de démonstration en cours...');
      console.log('📧 Email:', DEMO_ACCOUNT.email);
      
      const userCredential = await signInWithEmailAndPassword(
        auth, 
        DEMO_ACCOUNT.email, 
        DEMO_ACCOUNT.password
      );
      
      console.log('✅ Connecté au compte démo avec succès:', userCredential.user.email);
      console.log('🆔 UID du compte démo:', userCredential.user.uid);
      return userCredential.user;
      
    } catch (error) {
      console.error('❌ Erreur de connexion au compte démo:', error);
      console.error('📧 Email utilisé:', DEMO_ACCOUNT.email);
      console.error('🔢 Code d\'erreur:', error.code);
      console.error('💬 Message d\'erreur:', error.message);
      throw new Error(`Impossible de se connecter au compte démo: ${error.message}`);
    }
  }

  // Enregistrer un objectif de démonstration
  async saveDemoObjective(objectiveData) {
    console.log('💾 Tentative d\'enregistrement d\'objectif démo...');
    
    try {
      const user = auth.currentUser;
      console.log('👤 Utilisateur actuel:', user ? user.email : 'Aucun');
      
      if (!user) {
        console.warn('⚠️ Aucun utilisateur connecté');
        return { success: false, error: 'Aucun utilisateur connecté' };
      }
      
      if (user.email !== DEMO_ACCOUNT.email) {
        console.warn('⚠️ Utilisateur connecté non autorisé:', user.email);
        return { success: false, error: 'Compte démo requis' };
      }

      const demoData = {
        ...objectiveData,
        isDemoObjective: true,
        createdAt: serverTimestamp(),
        userId: user.uid,
        sessionId: this.generateSessionId(),
        visitorId: this.getOrCreateVisitorId(),
        userAgent: navigator.userAgent,
        referrer: document.referrer || '',
        timestamp: new Date().toISOString(),
      };

      console.log('📝 Données à enregistrer:', {
        userInput: demoData.userInput,
        source: demoData.source,
        userId: demoData.userId,
        sessionId: demoData.sessionId,
        success: demoData.success
      });

      const docRef = await addDoc(collection(db, 'objectives'), demoData);
      
      console.log('✅ Objectif démo enregistré avec succès!');
      console.log('🆔 ID du document:', docRef.id);
      
      return {
        success: true,
        objectiveId: docRef.id,
        message: 'Objectif enregistré avec succès'
      };

    } catch (error) {
      console.error('❌ Erreur lors de l\'enregistrement de l\'objectif démo:', error);
      console.error('📝 Détails de l\'erreur:', error.message);
      console.error('📊 Données tentées:', objectiveData);
      
      return {
        success: false,
        error: error.message,
        details: 'Voir la console pour plus de détails'
      };
    }
  }

  // MÉTHODE PRINCIPALE : Nettoyage immédiat de la session
  async cleanup() {
    try {
      if (auth.currentUser && auth.currentUser.email === DEMO_ACCOUNT.email) {
        console.log('🧹 Déconnexion immédiate du compte service...');
        await auth.signOut();
        this.isConnecting = false;
        this.connectionPromise = null;
        console.log('✅ Retour en mode visiteur - session nettoyée');
        
        // Déclencher un événement personnalisé pour notifier l'application
        window.dispatchEvent(new CustomEvent('demoSessionClosed', {
          detail: { message: 'Session démo fermée, retour en mode visiteur' }
        }));
        
        return true;
      } else {
        console.log('ℹ️ Aucune session démo active à nettoyer');
        return false;
      }
    } catch (error) {
      console.warn('⚠️ Erreur lors du nettoyage:', error);
      return false;
    }
  }

  // Forcer la déconnexion avant une vraie connexion utilisateur
  async ensureCleanStateForUserAuth() {
    console.log('🔄 Vérification de l\'état avant authentification utilisateur...');
    
    if (this.isDemoSession()) {
      console.log('🧹 Session démo détectée, nettoyage avant authentification utilisateur...');
      await this.cleanup();
      
      // Attendre un peu pour s'assurer que la déconnexion est effective
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    console.log('✅ État propre pour authentification utilisateur');
  }

  // Générer un ID de session unique
  generateSessionId() {
    const sessionId = `demo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    console.log('🔗 ID de session généré:', sessionId);
    return sessionId;
  }

  // Gérer l'ID visiteur (pour tracking anonyme)
  getOrCreateVisitorId() {
    let visitorId = localStorage.getItem('aladinn_visitor_id');
    if (!visitorId) {
      visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('aladinn_visitor_id', visitorId);
      console.log('🆔 Nouvel ID visiteur créé:', visitorId);
    } else {
      console.log('🆔 ID visiteur existant:', visitorId);
    }
    return visitorId;
  }

  // Vérifier si on est connecté au compte démo
  isDemoSession() {
    const currentUser = auth.currentUser;
    const isDemo = currentUser && currentUser.email === DEMO_ACCOUNT.email;
    return isDemo;
  }

  // Obtenir le statut de session
  getSessionStatus() {
    const currentUser = auth.currentUser;
    return {
      isDemo: this.isDemoSession(),
      isVisitor: !currentUser,
      isUserConnected: currentUser && currentUser.email !== DEMO_ACCOUNT.email,
      currentEmail: currentUser ? currentUser.email : null
    };
  }

  // Méthode de debugging
  getDebugInfo() {
    const currentUser = auth.currentUser;
    return {
      isConnecting: this.isConnecting,
      hasConnectionPromise: !!this.connectionPromise,
      currentUser: currentUser ? {
        uid: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName
      } : null,
      sessionStatus: this.getSessionStatus(),
      demoAccountEmail: DEMO_ACCOUNT.email
    };
  }
}

// Créer et exporter l'instance singleton
const demoLoginService = new DemoLoginService();

// Debug global (temporaire)
window.demoLoginService = demoLoginService;
console.log('🔧 DemoLoginService initialisé (version simplifiée)');

// Export par défaut
export default demoLoginService;